import { useState } from 'react';
import { toast } from 'react-toastify';
import { saveExercise, unsaveExercise } from '../utils/saveExercise';

/**
 * Custom hook for managing exercise bookmark/save functionality
 * @param {Object} user - Current user object
 * @param {Array} savedExerciseIds - Array of saved exercise IDs
 * @param {Function} setSavedExerciseIds - Function to update saved exercise IDs
 * @returns {Object} - Bookmark handlers and state
 */
export const useBookmark = (user, savedExerciseIds = [], setSavedExerciseIds = () => {}) => {
  const [isBookmarking, setIsBookmarking] = useState(false);

  const handleSaveExercise = async (exercise) => {
    if (!user) {
      // Save the current page and exercise info for post-login bookmark
      const pendingBookmarkData = {
        exerciseId: exercise.id,
        returnUrl: window.location.pathname,
        exerciseName: exercise.name
      };
      localStorage.setItem("pendingBookmark", JSON.stringify(pendingBookmarkData));
      // Also set a flag to indicate bookmark action was intended
      localStorage.setItem("pendingBookmarkAction", JSON.stringify({
        action: "bookmark",
        exerciseId: exercise.id,
        exerciseName: exercise.name
      }));
      window.location.href = "/login";
      return;
    }

    const isAlreadySaved = savedExerciseIds.includes(exercise.id);

    try {
      setIsBookmarking(true);
      if (isAlreadySaved) {
        await unsaveExercise(user, exercise.id);
        setSavedExerciseIds((prev) => prev.filter((id) => id !== exercise.id));
        toast.info(`${exercise.name} removed from your library.`);
      } else {
        const savedId = await saveExercise(user, exercise);
        if (savedId) {
          setSavedExerciseIds((prev) => [...prev, exercise.id]);
          toast.success(`${exercise.name} saved to your library!`);
        } else {
          toast.info(`${exercise.name} is already saved.`);
        }
      }
    } catch (err) {
      console.error("Error toggling save state:", err);
      toast.error("Something went wrong.");
    } finally {
      setIsBookmarking(false);
    }
  };

  const handleToggleSave = async (exerciseId, exerciseName) => {
    if (!user) return;

    try {
      setIsBookmarking(true);
      await unsaveExercise(user, exerciseId);
      setSavedExerciseIds((prev) => prev.filter((id) => id !== exerciseId));
      toast.info(`${exerciseName} removed from your library.`);
    } catch (err) {
      console.error("Failed to unsave exercise:", err);
      toast.error("Something went wrong.");
    } finally {
      setIsBookmarking(false);
    }
  };

  return {
    handleSaveExercise,
    handleToggleSave,
    isBookmarking
  };
};

export default useBookmark;
